import { useMemo } from 'react';
import { useContactBook, type WebContact, type WebOutreachThread } from './contact-book';
import { needsYouThreads, type PeopleWaitingThread } from './outreach-stage';

export type WebPeopleWaitingThread = PeopleWaitingThread<WebOutreachThread, WebContact>;

/**
 * The PEOPLE half of "waiting on you" (`native-apps/shared/outreach-needs-you/contract.json` rule 2): the distinct
 * NEEDS_YOU threads on the contact book's head. The head is `useContactBook('')` — the SAME cache entry the Inbox
 * draws its "Needs you" group from, which `prefetchContactBook` warms on app mount — so the pill, the quick-view's
 * People section and the Tasks screen's note read one list and state one number.
 *
 * Before the book has loaded (or when its read failed) `count` is 0 and `threads` empty: nothing is shown, never a
 * guessed number.
 */
export function usePeopleWaiting(): { count: number; threads: WebPeopleWaitingThread[]; entries: WebContact[] } {
  const book = useContactBook('');
  const contacts = book.kind === 'ready' ? book.contacts : undefined;
  const entries = useMemo(() => contacts ?? [], [contacts]);
  const threads = useMemo(() => needsYouThreads<WebOutreachThread, WebContact>(entries), [entries]);
  return { count: threads.length, threads, entries };
}
